import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CloseIconButton, CustomButton } from '../../components/buttons';
import CountdownTimer from '../../components/countdownTimer';
import Select from '../../components/inputs/select';
import Input from '../../components/inputs/text-field-input';
import { post } from '../../core/client';
import { IsNullOrEmpty, IsNullOrZero } from '../../core/utility';
import { PreQueryMemberInfoPhoneItem } from '../../models/query-member';
import { SendCodeWithSmsResponse } from '../../models/shared/Models';
import { QueryMemberContext } from './Context';

interface StateModel {
    communicationId: number | null;
    smsCode: string;
}

interface OtpModalProps {
    phones: PreQueryMemberInfoPhoneItem[];
    onSend: (communicationId: number, smsCode: string) => void;
}

interface InputErrorModel {
    name: string;
    errorText: string;
}

const OtpModal = (props: OtpModalProps) => {

    //context
    const context = useContext(QueryMemberContext);

    //definitions
    const { phones, onSend } = props;
    const initialState: StateModel = {
        communicationId: 0,
        smsCode: '',
    };

    //states
    const [state, setState] = useState<StateModel>(initialState);
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isSmsSended, setIsSmsSended] = useState<boolean>(false);
    const [isExpired, setIsExpired] = useState<boolean>(false);
    const [inputErrors, setInputErrors] = useState<Array<InputErrorModel>>([]);
    const [timeNow, setTimeNow] = useState(new Date().getTime());

    const TWO_MIN_IN_MS = 2 * 60 * 1000;
    const counterExpireTime = useMemo(() => timeNow + TWO_MIN_IN_MS, [timeNow]);

    //hooks    
    useEffect(() => {
        if (context.showOtpModal && phones.length === 1) {
            setState(prev => ({
                ...prev,
                communicationId: phones[0].id
            }))
        }
    }, [context.showOtpModal, phones]);

    //methods   
    const handleSendCode = async () => {
        if (!validate(state, true)) return;

        if (process.env.NODE_ENV !== 'production') {
            setIsSmsSended(true);
            setTimeNow(new Date().getTime());
            setErrorMessage('');
            return;
        }

        setLoading(true);
        setIsSmsSended(false);
        setIsExpired(false);
        setTimeNow(new Date().getTime());
        setErrorMessage('');
        const request = {
            communicationId: state.communicationId,
            goldenCode:context.member?.goldenCode,
            source: 'AvMemberQueryValidationOtp'
        }
        const response = await post<SendCodeWithSmsResponse>('api/general/send-validation-code', request);
        if (response && response.success) {
            setIsSmsSended(true);
        } else {
            setErrorMessage('Sms gönderilemedi');
        }
        setLoading(false);
    }

    const handleExpired = () => {
        setIsExpired(true);
    }

    const handleChange = (e: any) => {
        setState(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    }

    const handleClose = () => {
        setState(initialState);
        setIsSmsSended(false);
        setIsExpired(false);
        setErrorMessage('');
        setInputErrors([]);
        context.arrangeShowOtpModal(false);
    }

    const handleSend = () => {
        if (!validate(state, false)) return;

        onSend(state.communicationId as number, state.smsCode);
        handleClose();
    }

    const validate = (model: StateModel, resend: boolean): boolean => {

        setInputErrors([]);
        let isValid = true;

        if (IsNullOrZero(model.communicationId)) {
            const error: InputErrorModel = {
                name: 'communicationId',
                errorText: 'Telefon seçiniz'
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        if (!resend && IsNullOrEmpty(model.smsCode)) {
            const error: InputErrorModel = {
                name: 'smsCode',
                errorText: 'Bu alan boş bırakılamaz'
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }

        return isValid;
    };

    return (
        <Dialog maxWidth="sm" fullWidth={true} open={context.showOtpModal} onClose={handleClose} className="ks-modal">
            <DialogTitle className="modal-header" component="div">
                <h4 className="modal-title">Doğrulama Kodu</h4>
                <CloseIconButton onClick={handleClose} />
            </DialogTitle>
            <DialogContent>
                <Box mb={2} mt={2}>
                    <Select
                        id="communicationId"
                        name="communicationId"
                        caption="Telefon Numarası"
                        className="densed-select"
                        disabled={isSmsSended || phones.length === 1}
                        options={{
                            data: props.phones,
                            displayField: 'text',
                            displayValue: 'id',
                            selected: state.communicationId,
                        }}
                        onChange={handleChange}
                        errorText={inputErrors.filter(x => x.name === 'communicationId')[0]?.errorText}
                    />
                </Box>
                {isSmsSended && (
                    <Box mb={2}>
                        <Input
                            id="smsCode"
                            name="smsCode"
                            caption="Onay Kodu"
                            disabled={!isSmsSended}
                            value={state.smsCode}
                            onChange={handleChange}
                            errorText={inputErrors.filter(x => x.name === 'smsCode')[0]?.errorText}
                        />
                    </Box>
                )}
                {!IsNullOrEmpty(errorMessage) && (
                    <Box mb={2}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {isSmsSended ? (
                    <>
                        {!isExpired ? <CountdownTimer targetDate={counterExpireTime} onExpired={handleExpired} /> : <p>Doğrulama süresi doldu!</p>}
                        {isExpired && <CustomButton
                            text="TEKRAR KOD GÖNDER"
                            disabled={loading}
                            loading={loading}
                            className="btn-blue-alt ml-2"
                            variant="text"
                            onClick={handleSendCode}
                        />}
                        <CustomButton text="DOĞRULA" className="btn-primary" disabled={loading || isExpired} onClick={handleSend} />
                    </>
                ) : (
                    <CustomButton text="KOD GÖNDER" className="btn-primary" disabled={loading} loading={loading} onClick={handleSendCode} />
                )}
            </DialogActions>
        </Dialog>
    );
}

export default OtpModal;